import { useState, useEffect, useMemo } from 'react';
import { Megaphone, ChevronDown, ChevronUp, X } from 'lucide-react';
import { cn } from '@/lib/utils';

const VIEWED_KEY = 'solid-hub-viewed-announcements';
/** Announcements closed with the X. Remembered per browser, so a closed one
 *  stays closed across reloads — on the Dashboard and project pages alike. */
const DISMISSED_KEY = 'solid-hub-dismissed-announcements';
/** Ids are never pruned against the current list (the Dashboard and a project
 *  page show different lists), so cap the history instead. */
const MAX_REMEMBERED = 200;

function readIds(key: string): string[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(key) ?? '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function rememberIds(key: string, ids: string[]) {
  try {
    const merged = [...new Set([...readIds(key), ...ids])].slice(-MAX_REMEMBERED);
    localStorage.setItem(key, JSON.stringify(merged));
  } catch {
    // Storage unavailable (private window, blocked site data): dismissal lasts this visit only
  }
}

interface Announcement {
  id: string;
  title: string;
  content: string;
  startsAt: string;
  endsAt: string | null;
  createdAt: string;
  createdBy: { id: string; name: string; avatarUrl?: string };
}

interface AnnouncementBannerProps {
  announcements: Announcement[];
  className?: string;
}

export function AnnouncementBanner({ announcements: all, className }: AnnouncementBannerProps) {
  const [isAnimating, setIsAnimating] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [dismissedIds, setDismissedIds] = useState<string[]>(() => readIds(DISMISSED_KEY));

  // Memoised so the animation effect below doesn't restart its timer every render
  const announcements = useMemo(
    () => all.filter((a) => !dismissedIds.includes(a.id)),
    [all, dismissedIds],
  );

  const dismiss = () => {
    const ids = announcements.map((a) => a.id);
    rememberIds(DISMISSED_KEY, ids);
    setDismissedIds((prev) => [...prev, ...ids]);
  };

  // "New" = not seen in this browser yet
  const viewedAnnouncements = readIds(VIEWED_KEY);
  const hasNewAnnouncements = announcements.some(a => !viewedAnnouncements.includes(a.id));

  // Stop animation after initial view and mark as viewed
  useEffect(() => {
    if (announcements.length > 0 && hasNewAnnouncements) {
      const timer = setTimeout(() => {
        setIsAnimating(false);
        rememberIds(VIEWED_KEY, announcements.map(a => a.id));
      }, 3000); // Animate for 3 seconds

      return () => clearTimeout(timer);
    } else {
      setIsAnimating(false);
    }
  }, [announcements, hasNewAnnouncements]);

  if (announcements.length === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-xl border-2 border-amber-400/50 bg-gradient-to-r from-amber-50 via-yellow-50 to-amber-50',
        isAnimating && hasNewAnnouncements && 'animate-attention-pulse',
        className
      )}
    >
      {/* Dismiss button */}
      <button
        onClick={dismiss}
        className="absolute top-2 right-2 p-1 rounded-md text-amber-500 hover:text-amber-700 hover:bg-amber-100 transition-colors z-10"
        aria-label="Dismiss announcement"
      >
        <X className="w-4 h-4" />
      </button>

      {/* Animated background shimmer */}
      {isAnimating && hasNewAnnouncements && (
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-amber-200/30 to-transparent animate-shimmer" />
      )}

      <div className="relative">
        {announcements.map((announcement, index) => {
          const isExpanded = expandedId === announcement.id;
          // Strip HTML tags to get actual text length for "Read more" logic
          const textContent = announcement.content.replace(/<[^>]*>/g, '');
          const isLongContent = textContent.length > 200;

          return (
            <div
              key={announcement.id}
              className={cn(
                'px-4 py-3',
                index > 0 && 'border-t border-amber-200/50'
              )}
            >
              <div className="flex items-start gap-3">
                {/* Icon with pulse animation for new announcements */}
                <div
                  className={cn(
                    'flex-shrink-0 w-8 h-8 rounded-lg bg-amber-500 flex items-center justify-center shadow-lg',
                    isAnimating && hasNewAnnouncements && !viewedAnnouncements.includes(announcement.id) && 'animate-bounce'
                  )}
                >
                  <Megaphone className="w-4 h-4 text-white" />
                </div>

                <div className="flex-1 min-w-0">
                  {/* Title */}
                  <h3 className="font-semibold text-amber-900 text-sm leading-tight">
                    {announcement.title}
                  </h3>

                  {/* Content */}
                  <div
                    className={cn(
                      'mt-1 text-sm text-amber-800/80 prose prose-sm prose-amber max-w-none',
                      !isExpanded && isLongContent && 'line-clamp-2'
                    )}
                    dangerouslySetInnerHTML={{ __html: announcement.content }}
                  />

                  {/* Expand/Collapse for long content */}
                  {isLongContent && (
                    <button
                      onClick={() => setExpandedId(isExpanded ? null : announcement.id)}
                      className="mt-1 text-xs text-amber-600 hover:text-amber-700 font-medium flex items-center gap-0.5"
                    >
                      {isExpanded ? (
                        <>
                          Show less
                          <ChevronUp className="w-3 h-3" />
                        </>
                      ) : (
                        <>
                          Read more
                          <ChevronDown className="w-3 h-3" />
                        </>
                      )}
                    </button>
                  )}

                  {/* Meta info */}
                  <div className="mt-2 text-xs text-amber-600/70">
                    Posted by {announcement.createdBy?.name || 'Unknown'} · {new Date(announcement.startsAt).toLocaleDateString()}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Custom styles for animations */}
      <style>{`
        @keyframes attention-pulse {
          0%, 100% {
            box-shadow: 0 0 0 0 rgba(251, 191, 36, 0);
            transform: scale(1);
          }
          50% {
            box-shadow: 0 0 20px 4px rgba(251, 191, 36, 0.4);
            transform: scale(1.005);
          }
        }
        
        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }
        
        .animate-attention-pulse {
          animation: attention-pulse 1s ease-in-out infinite;
        }
        
        .animate-shimmer {
          animation: shimmer 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}


